import { captureException } from "@sentry/react"
import { isAxiosError } from "axios"
import { notification } from "hooks/toast"
import { MessageDescriptor, PrimitiveType } from "react-intl"
import { isSentryEnabled } from "utils/isSentryEnabled"
import { translate, translateMessage, translateTranslationEntry } from "utils/translate"

import { ErrorCode } from "@shared/enums/errorCode"
import { SerializedTranslationEntry, TranslationEntry } from "@shared/translations/translationEntry"

import { prettifyErrorData } from "./prettifyErrorData"

/**
 * Disable ESlint rules as we do not know the error response data type(s),
 * so we manually check (e.g., `isAxiosError` and `isRecord`)
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

/** Translates a react-intl message descriptor into a display string. */
type FormatFunction = (descriptor: MessageDescriptor, values?: Record<string, PrimitiveType>) => string

type OnResponseRejectedOptions = {
  /** Translates intl message IDs into user-facing strings (defaults to `translate`). */
  formatMessage?: FormatFunction
  /** Displays an error toast notification (defaults to `notification.error`). */
  showErrorToast?: typeof notification.error
  /** Error codes for which toast notifications should be suppressed (defaults to all `ErrorCode` values). */
  errorCodesNoMessage?: string[]
}

const defaultErrorMessage = "Internal Server Error"

const defaultOptions: Required<OnResponseRejectedOptions> = {
  formatMessage: translate,
  showErrorToast: notification.error,
  errorCodesNoMessage: Object.values(ErrorCode),
}

export const FALLBACK_ERROR_MESSAGE =
  "Can't load data from the server. Please wait a few seconds and try again. If it still fails, you may have to check your internet connection."

/**
 * Axios response-error interceptor.
 *
 * Handles rejected HTTP responses by showing user-facing error toasts and
 * re-throwing the original error so callers can handle it further.
 *
 * Error handling priority:
 * 1. Non-Axios errors or missing response — shows a generic fallback message
 *    and reports timeout/network errors to Sentry.
 * 2. 401 — delegates to the optional `handle401` callback (e.g. logout).
 * 3. Zod validation errors (400 with `data.errors[]` or `data.message[]`) —
 *    translates each issue via `translateMessage` and shows as separate toasts.
 * 4. Known HTTP statuses (404, 500, 508) — shows a fixed message.
 * 5. Responses with `fallbackCode` — shows a translated uncategorized-error
 *    message that includes the code for support reference.
 * 6. Responses with a `message` field — attempts intl translation, falls back
 *    to the raw string, or prettifies non-string payloads.
 * 7. Everything else — shows "Internal Server Error".
 *
 * Toast notifications are suppressed when `data.errorCode` matches one of the
 * codes in `errorCodesNoMessage`.
 */
export async function onResponseRejected(
  error: unknown,
  handle401?: (error: any) => void,
  options: OnResponseRejectedOptions = {}
): Promise<never> {
  const formatMessage = options.formatMessage ?? defaultOptions.formatMessage
  const showErrorToast = options.showErrorToast ?? defaultOptions.showErrorToast
  const errorCodesNoMessage = options.errorCodesNoMessage ?? defaultOptions.errorCodesNoMessage

  // In case the backend disappears (e.g. when we do a redeployment) users may
  // end up with strange errors. If that happens we show a fallback error message
  if (!isAxiosError(error) || !error.response) {
    showErrorToast({ message: FALLBACK_ERROR_MESSAGE })

    // IT-3583: Capture timeout and network errors to Sentry
    if (isAxiosError(error)) {
      const isTimeout = error.code === "ECONNABORTED" || error.message.includes("timeout")
      const isNetworkError = error.code === "ERR_NETWORK"

      if (isTimeout || isNetworkError) {
        if (isSentryEnabled()) {
          captureException(error, {
            tags: {
              errorType: isTimeout ? "axios_timeout" : "axios_network_error",
            },
            extra: {
              url: error.config?.url,
              method: error.config?.method,
              timeout: error.config?.timeout,
            },
          })
        } else {
          console.error(`[RESPONSE TIMEOUT]`, error)
        }
      }
    }

    throw error
  }

  const status = error.status || 500
  const data = error.response.data

  if (status === 401 && handle401) {
    handle401(error)
    throw error
  }

  const messages = getErrorMessages(status, data, formatMessage)

  for (const message of messages) {
    // Only show a notification if the code isn’t opted out
    const errorCode = typeof data?.errorCode === "string" ? data.errorCode : null
    const shouldShowToast = !errorCode || !errorCodesNoMessage.includes(data.errorCode)

    if (shouldShowToast) {
      showErrorToast({ message })
    }
  }

  throw error
}

/**
 * Extracts one or more user-facing error messages from a response payload.
 *
 * Zod validation errors arrive with serialized TranslationEntry strings as
 * their `.message` — the global error map produces these at parse-time.
 * This function extracts the message strings and passes them through
 * `translateMessage`, which handles deserialization and translation generically.
 */
function getErrorMessages(status: number, data: unknown, formatMessage: FormatFunction): string[] {
  if (status === 400 && isRecord(data)) {
    // Zod issues arrive in `errors` (ZodExceptionFilter) or `message` (BadRequestException via ZodValidationPipe)
    const issues =
      Array.isArray(data.errors) && data.errors?.length
        ? data.errors
        : Array.isArray(data.message) && data.message?.length
          ? data.message
          : undefined
    if (issues) {
      const messages = issues
        .filter((e: unknown): e is { message: string } => isRecord(e) && typeof e.message === "string")
        .map((e) => translateMessage(e.message))
      if (messages.length) {
        return messages
      }
    }
  }

  return [getErrorMessage(status, data, formatMessage)]
}

/** Derives a single user-facing error message from the HTTP status and response body. */
function getErrorMessage(status: number, data: unknown, formatMessage: FormatFunction): string {
  // Common HTTP statuses
  switch (status) {
    case 404:
      return "Not Found"
    case 500:
      // Check for fallbackCode - provides more context than generic message
      if (isRecord(data) && data.fallbackCode) {
        return formatMessage({ id: "general.uncategorizedError.message" }, { code: data.fallbackCode })
      }
      return "Internal Server Error"
    case 508:
      return "Time Out"
  }

  if (!isRecord(data)) {
    return defaultErrorMessage
  }

  if (data.fallbackCode) {
    return formatMessage({ id: "general.uncategorizedError.message" }, { code: data.fallbackCode })
  }

  if (data.translationEntry) {
    try {
      const entry = TranslationEntry.fromJSON(data.translationEntry as SerializedTranslationEntry)
      try {
        return translateTranslationEntry(entry, formatMessage)
      } catch {
        /* missing key or format failure — fall back to message */
      }
    } catch {
      /* invalid TranslationEntry payload */
    }
  }

  // If the payload carried a `message` field, try to translate it;
  // otherwise format it in a human-readable way.
  if (data.message) {
    const body = data.message
    if (typeof body === "string") {
      try {
        return formatMessage({ id: body })
      } catch {
        return body
      }
    }

    return prettifyErrorData(body)
  }

  return defaultErrorMessage
}

/** Type-guard that narrows `unknown` to a non-null object (record). */
function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === "object" && value !== null
}
